"use client"

/**
 * Pinned totals under the section Questions roster (Add questions + bank panel).
 *
 * One row only: icon + count for each fact. Need-review uses package
 * `StatusBadge` (danger); the label collapses to the count when the rail is
 * narrow. Same facts the assessment section header shows.
 */

import * as React from "react"

import { StatusBadge } from "@exxatdesignux/ui/components/status-badge"
import { Tip } from "@exxatdesignux/ui/components/tip"

import { cn } from "@/lib/utils"

function TotalsStat({
  icon,
  value,
  label,
  className,
}: {
  icon: string
  value: string | number
  label: string
  className?: string
}) {
  return (
    <Tip label={label}>
      <span
        className={cn(
          "inline-flex min-h-6 shrink-0 items-center gap-1.5 rounded-1 text-muted-foreground",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          className
        )}
        role="img"
        aria-label={label}
        tabIndex={0}
      >
        <i className={cn("fa-light text-xs", icon)} aria-hidden />
        <span className="font-semibold text-foreground tabular-nums">
          {value}
        </span>
      </span>
    </Tip>
  )
}

export function SectionQuestionsTotals({
  questionCount,
  totalPoints,
  estimatedMinutes,
  needsReviewCount,
  layout = "footer",
  className,
}: {
  questionCount: number
  totalPoints: number
  estimatedMinutes: number
  needsReviewCount: number
  /** `footer` pins under a roster. `inline` sits in a section header. */
  layout?: "footer" | "inline"
  className?: string
}) {
  if (questionCount === 0) return null

  const reviewLabel = `${needsReviewCount} need review`
  const inline = layout === "inline"

  return (
    <div
      className={cn(
        "flex shrink-0 flex-nowrap items-center text-xs",
        inline
          ? "gap-4"
          : "@container/totals gap-3 overflow-hidden border-t border-border px-3 py-2",
        className
      )}
    >
      {needsReviewCount > 0 ? (
        inline ? (
          <Tip label={reviewLabel}>
            <StatusBadge
              label={String(needsReviewCount)}
              icon="fa-triangle-exclamation"
              tone="danger"
              size="md"
              tabIndex={0}
              aria-label={reviewLabel}
              className="shrink-0"
            />
          </Tip>
        ) : (
          <>
            {/* Narrow rail: icon + count. Tip + aria-label keep the full meaning. */}
            <Tip label={reviewLabel}>
              <StatusBadge
                label={String(needsReviewCount)}
                icon="fa-triangle-exclamation"
                tone="danger"
                size="md"
                tabIndex={0}
                aria-label={reviewLabel}
                className="@[16rem]/totals:hidden"
              />
            </Tip>
            <Tip label={reviewLabel}>
              <StatusBadge
                label={reviewLabel}
                icon="fa-triangle-exclamation"
                tone="danger"
                size="md"
                tabIndex={0}
                className="hidden @[16rem]/totals:inline-flex"
              />
            </Tip>
          </>
        )
      ) : null}

      <TotalsStat
        icon="fa-list-ol"
        value={questionCount}
        label={`${questionCount} ${questionCount === 1 ? "question" : "questions"}`}
      />

      <TotalsStat
        icon="fa-clock"
        value={estimatedMinutes}
        label={`Est. time ${estimatedMinutes} min`}
      />

      <TotalsStat
        icon="fa-bullseye"
        value={totalPoints}
        label={`${totalPoints} ${totalPoints === 1 ? "mark" : "marks"}`}
        className={inline ? undefined : "ms-auto"}
      />
    </div>
  )
}
